import { useMemo, useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { MENU, CONTACT } from "@/data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Order() {
  const [cart, setCart] = useState({});
  const [details, setDetails] = useState({ name: "", phone: "", address: "", note: "" });
  const [placing, setPlacing] = useState(false);

  const add = (name, price) => setCart((c) => ({ ...c, [name]: { name, price, qty: (c[name]?.qty || 0) + 1 } }));
  const remove = (name) =>
    setCart((c) => {
      const qty = (c[name]?.qty || 0) - 1;
      const next = { ...c };
      if (qty <= 0) delete next[name];
      else next[name] = { ...next[name], qty };
      return next;
    });

  const items = useMemo(() => Object.values(cart), [cart]);
  const total = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);

  const update = (k) => (e) => setDetails({ ...details, [k]: e.target.value });

  const buildMessage = () => {
    const lines = items.map((i) => `• ${i.name} x${i.qty} — ₹${i.price * i.qty}`);
    return [
      "New Order — GOAT Sliders",
      "",
      ...lines,
      "",
      `Total: ₹${total}`,
      "",
      `Name: ${details.name}`,
      `Phone: ${details.phone}`,
      `Address: ${details.address}`,
      details.note ? `Note: ${details.note}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Add at least one item to your order.");
      return;
    }
    setPlacing(true);
    try {
      await axios.post(`${API}/orders`, {
        customer_name: details.name,
        phone: details.phone,
        address: details.address,
        items: items.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
        total,
        note: details.note || null,
      });
      window.open(`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(buildMessage())}`, "_blank");
      toast.success("Order placed! Confirming on WhatsApp.");
      setCart({});
      setDetails({ name: "", phone: "", address: "", note: "" });
    } catch {
      toast.error("Could not save your order. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  const inputStyle = {
    background: "var(--goat-cream)",
    border: "2.5px solid var(--goat-green)",
    color: "var(--goat-green)",
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="order-page">
      <h1 className="text-5xl md:text-6xl uppercase tracking-tighter" style={{ color: "var(--goat-green)" }} data-testid="order-heading">
        Place Your Order
      </h1>
      <p className="mt-4 text-lg md:text-xl max-w-xl" style={{ color: "var(--goat-green)" }} data-testid="order-subtext">
        Build your order below — it lands straight on our WhatsApp, fresh off the grill.
      </p>

      <div className="mt-12 grid lg:grid-cols-5 gap-8">
        {/* Items picker */}
        <div className="lg:col-span-3 space-y-10">
          {Object.entries(MENU).map(([key, section]) => (
            <section key={key} data-testid={`order-section-${key}`}>
              <h2 className="text-3xl uppercase mb-5" style={{ color: "var(--goat-orange)" }} data-testid={`order-section-heading-${key}`}>
                {section.title}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {section.items.map((item) => {
                  const qty = cart[item.name]?.qty || 0;
                  const tid = item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                  return (
                    <div key={item.name} className="sticker-card p-5 flex items-center justify-between gap-3" data-testid={`order-item-${tid}`}>
                      <div>
                        <p className="text-base font-medium" style={{ color: "var(--goat-green)" }}>{item.name}</p>
                        <p className="font-display text-lg" style={{ color: "var(--goat-orange)" }}>₹{item.price}</p>
                      </div>
                      {qty === 0 ? (
                        <button
                          onClick={() => add(item.name, item.price)}
                          className="sticker-btn px-4 py-2 font-display uppercase text-sm"
                          style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }}
                          data-testid={`order-add-${tid}`}
                          aria-label={`Add ${item.name}`}
                        >
                          Add
                        </button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button onClick={() => remove(item.name)} className="badge-sticker p-2" style={{ background: "var(--goat-white)" }} data-testid={`order-minus-${tid}`} aria-label={`Remove one ${item.name}`}>
                            <Minus size={16} color="#024423" />
                          </button>
                          <span className="font-display text-lg w-6 text-center" style={{ color: "var(--goat-green)" }} data-testid={`order-qty-${tid}`}>{qty}</span>
                          <button onClick={() => add(item.name, item.price)} className="badge-sticker p-2" style={{ background: "var(--goat-orange)" }} data-testid={`order-plus-${tid}`} aria-label={`Add one more ${item.name}`}>
                            <Plus size={16} color="#024423" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Cart + details */}
        <div className="lg:col-span-2">
          <div className="sticky top-28 space-y-6">
            <div className="sticker-card p-6" style={{ background: "var(--goat-green)" }} data-testid="order-cart-card">
              <h2 className="text-2xl uppercase mb-4" style={{ color: "var(--goat-cream)" }}>Your Order</h2>
              {items.length === 0 ? (
                <p className="text-base" style={{ color: "var(--goat-cream)" }} data-testid="order-cart-empty">
                  Your tray is empty — add some sliders!
                </p>
              ) : (
                <>
                  <ul className="space-y-2">
                    {items.map((i) => (
                      <li key={i.name} className="flex items-center justify-between gap-2 text-base" style={{ color: "var(--goat-cream)" }} data-testid={`cart-item-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <span>{i.name} x{i.qty}</span>
                        <span className="flex items-center gap-2">
                          <span>₹{i.price * i.qty}</span>
                          <button onClick={() => setCart((c) => { const n = { ...c }; delete n[i.name]; return n; })} aria-label={`Remove ${i.name}`} data-testid={`cart-remove-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <Trash2 size={15} color="#FE5E09" />
                          </button>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 pt-4 flex justify-between font-display text-xl" style={{ borderTop: "2px dashed var(--goat-orange)", color: "var(--goat-cream)" }} data-testid="order-total">
                    <span>Total</span>
                    <span>₹{total}</span>
                  </div>
                </>
              )}
            </div>

            <form onSubmit={placeOrder} className="sticker-card p-6 space-y-4" data-testid="order-form">
              <h2 className="text-2xl uppercase" style={{ color: "var(--goat-green)" }}>Your details</h2>
              <div>
                <label className="block text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--goat-green)" }} htmlFor="order-name">Name</label>
                <input id="order-name" required value={details.name} onChange={update("name")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="order-name-input" />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--goat-green)" }} htmlFor="order-phone">Phone</label>
                <input id="order-phone" required type="tel" value={details.phone} onChange={update("phone")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="order-phone-input" />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--goat-green)" }} htmlFor="order-address">Delivery Address</label>
                <textarea id="order-address" required rows={3} value={details.address} onChange={update("address")} className="w-full px-4 py-3 rounded-xl text-base outline-none resize-none" style={inputStyle} data-testid="order-address-input" />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-widest font-semibold mb-1" style={{ color: "var(--goat-green)" }} htmlFor="order-note">Note (optional)</label>
                <input id="order-note" value={details.note} onChange={update("note")} className="w-full px-4 py-3 rounded-xl text-base outline-none" style={inputStyle} data-testid="order-note-input" />
              </div>
              <button
                type="submit"
                disabled={placing}
                className="sticker-btn w-full px-6 py-4 font-display uppercase text-base flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ background: "var(--goat-orange)", color: "var(--goat-white)" }}
                data-testid="order-submit-btn"
              >
                <MessageCircle size={18} /> {placing ? "Placing..." : `Order on WhatsApp${total ? ` — ₹${total}` : ""}`}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
